import React, {
  useState, useRef, useEffect, useMemo, useCallback, memo,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { usePopperTooltip } from 'react-popper-tooltip';
import { getColorOptionForBlock, BLOCK_COLOR_OPTIONS } from './blockColors';
import BlockContent from './presentational/BlockContent';
import usePlannerResize from '../hooks/usePlannerResize';

const ColorToolbar = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px;
  background: #FFFFFF;
  border-radius: 4px;
  border: 1px solid #eeeeee;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  pointer-events: auto;

  /* Arrow pointing down to the block (when toolbar is above) */
  &[data-popper-placement^='top'] > .arrow {
    bottom: -4px;

    &::before {
      border-width: 6px 6px 0 6px;
      border-color: #eeeeee transparent transparent transparent;
    }

    &::after {
      border-width: 5px 5px 0 5px;
      border-color: #FFFFFF transparent transparent transparent;
      bottom: 1px;
    }
  }

  /* Arrow pointing up to the block (when toolbar is below) */
  &[data-popper-placement^='bottom'] > .arrow {
    top: -4px;

    &::before {
      border-width: 0 6px 6px 6px;
      border-color: transparent transparent #eeeeee transparent;
    }

    &::after {
      border-width: 0 5px 5px 5px;
      border-color: transparent transparent #FFFFFF transparent;
      top: 1px;
    }
  }
`;

const Arrow = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-style: solid;
  }
`;

const ColorButton = styled.button`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: ${(props) => {
    if (props.isSelected) {
      return `2px solid ${props.accentColor}`;
    }
    return `1px solid ${props.borderColor}`;
  }};
  background: ${(props) => props.backgroundColor};
  cursor: pointer;
  transition: all 0.1s ease;
  padding: 0;
  outline: none;
  box-shadow: none;
  position: relative;

  &:hover {
    transform: scale(1.15);
  }

  &:active {
    transform: scale(0.9);
  }
`;

const CheckMark = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${(props) => props.accentColor};
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  pointer-events: none;
`;

/**
 * Block wrapper for positioning and drag/resize handling
 */
const BlockWrapper = styled.div`
  position: absolute;
  box-sizing: border-box;
  cursor: ${(props) => (props.disabled ? 'default' : 'grab')};
  touch-action: none;
  user-select: none;

  /* Performance optimizations */
  will-change: ${(props) => (props.isDragging || props.isResizing ? 'transform, width, left' : 'auto')};
  transform: translateZ(0);
  backface-visibility: hidden;

  /* Visual feedback during drag */
  opacity: ${(props) => (props.isDragging ? 0.5 : 1)};
  transition: ${(props) => (props.isDragging || props.isResizing ? 'none' : 'opacity 0.1s ease')};
`;

/**
 * Resize handle component
 */
const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 36px;
  cursor: ${(props) => (props.position === 'left' ? 'w-resize' : 'e-resize')};
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Position based on side - extends into block for easier grabbing */
  ${(props) => (props.position === 'left' ? 'left: -12px;' : 'right: -12px;')}

  /* Visual indicator */
  &::after {
    content: '';
    width: 4px;
    height: 24px;
    background: ${(props) => (props.isActive ? props.accentColor : 'transparent')};
    border-radius: 2px;
    transition: background 0.15s ease;
  }

  /* Show indicator on hover */
  &:hover::after {
    background: ${(props) => props.accentColor || '#77A7FF'};
  }

  /* Touch-friendly target */
  @media (hover: none) {
    width: 48px;
    ${(props) => (props.position === 'left' ? 'left: -24px;' : 'right: -24px;')}
  }
`;

/**
 * Touch arrow indicator for mobile devices
 */
const TouchArrow = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.isActive ? `${props.accentColor}CC` : `${props.accentColor}99`)};
  color: white;
  font-size: 22px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
  cursor: ew-resize;
  user-select: none;
  touch-action: none;
  z-index: 10003;
  transition: all 0.2s ease;

  ${(props) => (props.position === 'left' ? 'left: -20px;' : 'right: -20px;')}

  ${(props) => props.isActive && `
    transform: translateY(-50%) scale(1.2);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  `}
`;

/**
 * PlanBlock Component
 *
 * A draggable, resizable block in the plan grid using @dnd-kit and custom resize hooks.
 * Supports inline name editing, drag to reposition, and edge resize.
 * Shows visual feedback when overlapping with other blocks.
 */
const PlanBlock = ({
  block,
  weeksLength,
  cellWidth,
  selected,
  hasOverlap,
  overlappingCount,
  showOverlapIndicator,
  isOnline,
  onSelect,
  onResize,
  onNameChange,
  onEditingStart,
  onEditingEnd,
  onColorChange,
  // Pre-computed position props from parent
  startCol: propStartCol,
  endCol: propEndCol,
  weekSpan: propWeekSpan,
  x: propX,
  y: propY,
  width: propWidth,
  height: propHeight,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempName, setTempName] = useState(block.name);
  const [isColorToolbarOpen, setIsColorToolbarOpen] = useState(false);
  const [tempColorId, setTempColorId] = useState(null);
  const inputRef = useRef(null);
  const textRef = useRef(null);
  const hasCalledOnNameChangeRef = useRef(false);
  const editingStartNameRef = useRef(block.name);
  const blurTimeoutRef = useRef(null);
  const lastTapTimeRef = useRef(0);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  // Use Popper for color toolbar positioning
  const {
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    visible: popperVisible,
  } = usePopperTooltip({
    trigger: null,
    visible: isColorToolbarOpen,
    placement: 'top',
    offset: [0, 8],
  });

  // Use temporary color during edit mode for immediate feedback
  const palette = useMemo(() => {
    if (isEditing && tempColorId) {
      return getColorOptionForBlock({ ...block, colorId: tempColorId });
    }
    return getColorOptionForBlock(block);
  }, [block, isEditing, tempColorId]);

  // Detect if device supports touch
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const checkTouch = () => {
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsTouchDevice(hasTouch);
    };

    checkTouch();
    window.addEventListener('resize', checkTouch);
    return () => window.removeEventListener('resize', checkTouch);
  }, []);

  // Cleanup timeouts on unmount
  useEffect(
    () => () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    },
    [],
  );

  // Position values come pre-computed from parent
  const startCol = propStartCol;
  const endCol = propEndCol;
  const weekSpan = propWeekSpan;

  // Use @dnd-kit for dragging
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: block.id,
    data: {
      block,
      weekSpan,
      startCol,
      endCol,
    },
    disabled: !isOnline || isEditing,
  });

  // Use custom resize hook
  const {
    isResizing,
    resizeDirection,
    displayX,
    displayWidth,
    leftHandleProps,
    rightHandleProps,
  } = usePlannerResize({
    blockId: block.id,
    startCol,
    endCol,
    x: propX,
    width: propWidth,
    cellWidth,
    weeksLength,
    onResize: (resizedBlockId, newStartCol, newEndCol) => {
      if (newStartCol !== startCol || newEndCol !== endCol) {
        onResize(resizedBlockId, newStartCol, newEndCol);
      }
    },
    disabled: !isOnline || !selected,
  });

  // Compute final position with drag transform and resize adjustments
  const x = isResizing ? displayX : propX;
  const y = propY;
  const width = isResizing ? displayWidth : propWidth;
  const height = propHeight;

  // Apply drag transform
  const style = {
    left: x,
    top: y,
    width,
    height,
    transform: transform ? CSS.Transform.toString(transform) : undefined,
  };

  let blockZIndex = 4;
  if (selected) {
    blockZIndex = 20;
  } else if (hasOverlap) {
    blockZIndex = 8;
  }
  if (isDragging) {
    blockZIndex = 1000;
  }

  // Handler functions
  const handleFinishEditing = useCallback(() => {
    const newName = tempName.trim();
    if (
      newName !== editingStartNameRef.current
      && !hasCalledOnNameChangeRef.current
    ) {
      hasCalledOnNameChangeRef.current = true;
      onNameChange(block.id, newName);
    }
    setIsEditing(false);
  }, [tempName, block.id, onNameChange]);

  const handleBlur = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = setTimeout(() => {
      handleFinishEditing();
    }, 150);
  }, [handleFinishEditing]);

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      // Selection is handled in mouseDown, click just stops propagation
    },
    [],
  );

  const handleMouseDown = useCallback((e) => {
    if (!isEditing) {
      // Pass event to support multi-select with Shift
      onSelect(block.id, e);
    }
  }, [isEditing, block.id, onSelect]);

  const handleDoubleClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (!isOnline || isDragging) return;
      setIsEditing(true);
    },
    [isOnline, isDragging],
  );

  /**
   * Handle touch end for double-tap detection on touch devices.
   * Two taps within 300ms triggers edit mode.
   */
  const handleTouchEnd = useCallback(
    (e) => {
      if (!isOnline || isDragging || isEditing) return;

      const now = Date.now();
      const timeSinceLastTap = now - lastTapTimeRef.current;

      if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
        // Double tap detected - enter edit mode
        e.preventDefault();
        e.stopPropagation();
        setIsEditing(true);
        lastTapTimeRef.current = 0; // Reset to prevent triple-tap
      } else {
        lastTapTimeRef.current = now;
      }
    },
    [isOnline, isDragging, isEditing],
  );

  const handleColorSelect = useCallback(
    (e, colorId) => {
      e.stopPropagation();
      e.preventDefault();
      if (!isOnline) return;
      setTempColorId(colorId);
      onColorChange(block.id, colorId);
    },
    [block.id, onColorChange, isOnline],
  );

  const handleColorMouseDown = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleContextMenu = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Enter edit mode only when first selected after creation
  const prevSelectedRef = useRef(selected);
  const hasBeenSelectedRef = useRef(false);
  useEffect(() => {
    if (
      selected
      && !prevSelectedRef.current
      && !hasBeenSelectedRef.current
      && !block.name?.trim()
      && !isDragging
    ) {
      hasBeenSelectedRef.current = true;
      setIsEditing(true);
    }
    prevSelectedRef.current = selected;
  }, [selected, block.name, isDragging]);

  useEffect(() => {
    if (isEditing) {
      hasCalledOnNameChangeRef.current = false;
      editingStartNameRef.current = block.name;
      onEditingStart(block.id);
      setIsColorToolbarOpen(true);
      setTempColorId(block.colorId);
    } else {
      onEditingEnd();
      setIsColorToolbarOpen(false);
      setTempColorId(null);
    }
  }, [
    isEditing,
    block.id,
    block.name,
    block.colorId,
    onEditingStart,
    onEditingEnd,
  ]);

  const prevSelectedForDeselect = useRef(selected);
  useEffect(() => {
    if (!selected && prevSelectedForDeselect.current) {
      setIsColorToolbarOpen(false);
      if (isEditing) {
        handleFinishEditing();
      }
    }
    prevSelectedForDeselect.current = selected;
  }, [selected, isEditing, handleFinishEditing]);

  useEffect(() => {
    if (!selected) {
      return undefined;
    }

    const handleKeyDown = (e) => {
      // Note: Delete/Backspace is handled by useGridNavigation for multi-delete support
      if (e.key === 'Enter') {
        if (isEditing) {
          e.preventDefault();
          handleFinishEditing();
        } else {
          setIsEditing(true);
        }
      } else if (e.key === 'Escape') {
        if (isEditing) {
          setTempName(block.name || '');
          setIsEditing(false);
        } else {
          e.preventDefault();
          onSelect(null);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    selected,
    isEditing,
    block.name,
    tempName,
    handleFinishEditing,
    onSelect,
  ]);

  useEffect(() => {
    if (!isEditing && textRef.current) {
      const isTruncated = textRef.current.scrollWidth > textRef.current.clientWidth;
      setIsTextTruncated(isTruncated);
    }
  }, [block.name, isEditing, width]);

  // Don't render if block weeks are not in visible range
  if (startCol === -1 || endCol === -1) {
    return null;
  }

  return (
    <>
      <BlockWrapper
        ref={setNodeRef}
        style={{ ...style, zIndex: blockZIndex }}
        isDragging={isDragging}
        isResizing={isResizing}
        disabled={!isOnline || isEditing}
        {...(isEditing || isResizing ? {} : attributes)}
        {...(isEditing || isResizing ? {} : listeners)}
      >
        <BlockContent
          ref={setTriggerRef}
          palette={palette}
          isEditing={isEditing}
          isSelected={selected}
          isDragging={isDragging}
          name={block.name}
          tempName={tempName}
          isTextTruncated={isTextTruncated}
          hasOverlap={hasOverlap}
          showOverlapIndicator={showOverlapIndicator}
          overlappingCount={overlappingCount}
          textRef={textRef}
          inputRef={inputRef}
          onClick={handleClick}
          onMouseDownCapture={handleMouseDown}
          onDoubleClick={handleDoubleClick}
          onTouchEnd={handleTouchEnd}
          onContextMenu={handleContextMenu}
          onNameChange={(e) => setTempName(e.target.value)}
          onInputBlur={handleBlur}
          onInputKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleFinishEditing();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setTempName(block.name || '');
              setIsEditing(false);
            }
          }}
          onInputClick={(e) => e.stopPropagation()}
          onInputMouseDown={(e) => e.stopPropagation()}
        />

        {/* Resize handles - only show when selected */}
        {selected && !isEditing && isOnline && (
          isTouchDevice ? (
            <>
              <TouchArrow
                position='left'
                accentColor={palette.accent}
                isActive={isResizing && resizeDirection === 'left'}
                {...leftHandleProps}
              >
                ←
              </TouchArrow>
              <TouchArrow
                position='right'
                accentColor={palette.accent}
                isActive={isResizing && resizeDirection === 'right'}
                {...rightHandleProps}
              >
                →
              </TouchArrow>
            </>
          ) : (
            <>
              <ResizeHandle
                position='left'
                accentColor={palette.accent}
                isActive={isResizing && resizeDirection === 'left'}
                {...leftHandleProps}
              />
              <ResizeHandle
                position='right'
                accentColor={palette.accent}
                isActive={isResizing && resizeDirection === 'right'}
                {...rightHandleProps}
              />
            </>
          )
        )}
      </BlockWrapper>

      {/* Color toolbar - positioned with Popper */}
      {popperVisible && (
        <ColorToolbar ref={setTooltipRef} {...getTooltipProps()}>
          <Arrow className='arrow' />
          {BLOCK_COLOR_OPTIONS.map((option) => {
            const currentColorId = tempColorId || block.colorId;
            const isCurrentColor = option.id === currentColorId
              || (!currentColorId && option.id === 'lightblue');
            return (
              <ColorButton
                key={option.id}
                type='button'
                backgroundColor={option.background}
                borderColor={option.border}
                accentColor={option.accent}
                isSelected={isCurrentColor}
                onMouseDown={handleColorMouseDown}
                onClick={(e) => handleColorSelect(e, option.id)}
                title={option.label}
              >
                {isCurrentColor && (
                  <CheckMark accentColor={option.accent}>✓</CheckMark>
                )}
              </ColorButton>
            );
          })}
        </ColorToolbar>
      )}
    </>
  );
};

PlanBlock.propTypes = {
  block: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    fromWeek: PropTypes.string.isRequired,
    toWeek: PropTypes.string.isRequired,
    row: PropTypes.number.isRequired,
    colorId: PropTypes.string,
  }).isRequired,
  originalBlock: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    fromWeek: PropTypes.string.isRequired,
    toWeek: PropTypes.string.isRequired,
    row: PropTypes.number.isRequired,
    colorId: PropTypes.string,
  }).isRequired,
  weeks: PropTypes.arrayOf(PropTypes.string).isRequired,
  weeksLength: PropTypes.number.isRequired,
  cellWidth: PropTypes.number.isRequired,
  cellHeight: PropTypes.number.isRequired,
  rowHeight: PropTypes.number.isRequired,
  maxRows: PropTypes.number.isRequired,
  selected: PropTypes.bool,
  hasOverlap: PropTypes.bool,
  overlappingCount: PropTypes.number,
  showOverlapIndicator: PropTypes.bool,
  isOnline: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onResize: PropTypes.func.isRequired,
  onNameChange: PropTypes.func.isRequired,
  onEditingStart: PropTypes.func.isRequired,
  onEditingEnd: PropTypes.func.isRequired,
  onColorChange: PropTypes.func.isRequired,
  startCol: PropTypes.number,
  endCol: PropTypes.number,
  weekSpan: PropTypes.number,
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
};

PlanBlock.defaultProps = {
  selected: false,
  hasOverlap: false,
  overlappingCount: 0,
  showOverlapIndicator: false,
  isOnline: true,
  startCol: undefined,
  endCol: undefined,
  weekSpan: undefined,
  x: undefined,
  y: undefined,
  width: undefined,
  height: undefined,
};

// Prop keys for comparison
const PRIMITIVE_KEYS = [
  'selected', 'hasOverlap', 'overlappingCount', 'showOverlapIndicator',
  'isOnline', 'cellWidth', 'cellHeight', 'rowHeight', 'maxRows', 'weeksLength',
  'x', 'y', 'width', 'height', 'startCol', 'endCol', 'weekSpan',
];
const BLOCK_KEYS = ['id', 'name', 'fromWeek', 'toWeek', 'row', 'colorId'];
const ORIGINAL_BLOCK_KEYS = ['fromWeek', 'toWeek', 'row'];

const shallowEqual = (a, b, keys) => keys.every((k) => a[k] === b[k]);

const areEqual = (prev, next) => (
  shallowEqual(prev, next, PRIMITIVE_KEYS)
  && shallowEqual(prev.block, next.block, BLOCK_KEYS)
  && shallowEqual(prev.originalBlock, next.originalBlock, ORIGINAL_BLOCK_KEYS)
  && prev.weeks === next.weeks
);

export default memo(PlanBlock, areEqual);
